/**
 * Billing currency selection — pure logic, unit-tested without a DOM.
 *
 * Policy: customers pay in USD unless they're Australian (AUD) or Japanese
 * (JPY). Detection is browser-side (timezone first, then language) and the
 * server's /api/billing/status says which currencies actually have a
 * Chargebee item price configured; anything unavailable falls back to USD,
 * then to whatever the server's default charges.
 */

export const PRICES = {
  USD: { screen: 'US$6', free: 'US$0' },
  AUD: { screen: 'A$9', free: 'A$0' },
  JPY: { screen: '¥900', free: '¥0' },
};

// What the timezone says: 'AUD' | 'JPY' | null (no opinion).
export function currencyFromTimeZone(timeZone) {
  const tz = String(timeZone || '');
  if (tz.startsWith('Australia/')) return 'AUD';
  if (tz === 'Asia/Tokyo') return 'JPY';
  return null;
}

// What the preferred languages say: 'AUD' | 'JPY' | null (no opinion).
export function currencyFromLanguages(languages) {
  for (const lang of Array.isArray(languages) ? languages : []) {
    const l = String(lang || '').toLowerCase();
    if (l === 'ja' || l.startsWith('ja-')) return 'JPY';
    if (l.endsWith('-au')) return 'AUD';
  }
  return null;
}

// Compare both signals -> { currency, ambiguous }. Ambiguous when they
// disagree (Australian timezone + Japanese language, or an AU/JP language
// with an overseas timezone) — the UI then offers a manual choice. A signal
// with no opinion never makes things ambiguous.
export function resolveCurrency(timeZone, languages) {
  const tzCur = currencyFromTimeZone(timeZone);
  const langCur = currencyFromLanguages(languages);
  if (tzCur && langCur && tzCur !== langCur) return { currency: tzCur, ambiguous: true };
  if (tzCur) return { currency: tzCur, ambiguous: false };
  if (langCur) return { currency: langCur, ambiguous: true };
  return { currency: 'USD', ambiguous: false };
}

// Timezone/languages -> 'USD' | 'AUD' | 'JPY'.
export function detectCurrency(timeZone, languages) {
  return resolveCurrency(timeZone, languages).currency;
}

// Detected currency constrained to what the server offers.
export function pickCurrency(detected, available) {
  if (!Array.isArray(available) || available.length === 0) return detected;
  if (available.includes(detected)) return detected;
  if (available.includes('USD')) return 'USD';
  return available[0];
}

// Price labels for a currency, defaulting to USD for anything unknown.
export function priceFor(currency) {
  return PRICES[currency] || PRICES.USD;
}

// Browser entry point: reads Intl/navigator, never throws.
export function detectBrowserCurrencyInfo() {
  let tz = '';
  try { tz = Intl.DateTimeFormat().resolvedOptions().timeZone || ''; } catch (_) { /* no Intl */ }
  const langs = (typeof navigator !== 'undefined' && (navigator.languages || [navigator.language])) || [];
  return resolveCurrency(tz, langs);
}

export function detectBrowserCurrency() {
  return detectBrowserCurrencyInfo().currency;
}
